import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

/** Premium animated hero for the client dashboard.
 *
 *  Three motion-driven variants tied to ride status:
 *
 *  - `idle`      → no active ride. Gentle aurora gradient + drifting orbs,
 *                  faint Price logomark in the center, sparkle accent.
 *
 *  - `searching` → ride requested, waiting for a driver to accept.
 *                  SONAR RADAR : 4 concentric rings expanding from the
 *                  centre (staggered), elegant inline-SVG car silhouettes
 *                  that fade in / out around the radar like detected
 *                  vehicles, a small centre badge with the Price wordmark
 *                  and a typewriter "Recherche d'un chauffeur…" caption.
 *
 *  - `tracking`  → driver assigned and en route. Route S-curve drawn on
 *                  mount + soft driver beacon riding the curve.
 *
 *  Pure SVG + framer-motion. Zero network, zero canvas, zero workers →
 *  butter-smooth on iOS WKWebView and every other webview / browser.
 */
export default function ClientHero({ mode = "idle", driverActive = false }) {
  // Subtle parallax — only on devices with a mouse to keep mobile snappy
  // and battery-friendly. The orbs drift a few pixels following the cursor.
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const isTouch = typeof window !== "undefined" && "ontouchstart" in window;
    if (isTouch) return;
    const handler = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      setParallax({
        x: ((e.clientX || w / 2) / w - 0.5) * 18,
        y: ((e.clientY || h / 2) / h - 0.5) * 18,
      });
    };
    window.addEventListener("mousemove", handler, { passive: true });
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="client-hero"
    >
      {/* Base gradient — deeper Jewel & Luxury palette. Slightly darker
          top-stop (evening emerald) grounds the SAOR mark and makes the
          3D vehicle renders on the sheet below feel like they sit on a
          designed backdrop, not raw canvas. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 65% at 50% 0%, #a5c9c5 0%, #d6e5e0 22%, #f0eadb 55%, #eadcc0 100%)",
        }}
      />
      {/* Subtle noise/grain overlay — hand-crafted feel without a real
          image dep. Uses a fine SVG turbulence rendered inline so the
          filter is applied entirely on the GPU (no repaint jank). */}
      <div
        aria-hidden
        className="absolute inset-0 mix-blend-overlay opacity-[0.22]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "200px 200px",
        }}
      />

      {/* Aurora orbs — drift + breathe. */}
      <motion.div
        aria-hidden
        className="absolute -top-24 -left-20 h-[420px] w-[420px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(14,92,91,0.45) 0%, rgba(14,92,91,0) 70%)",
          willChange: "transform",
        }}
        animate={{
          x: [parallax.x - 10, parallax.x + 10, parallax.x - 10],
          y: [parallax.y - 8, parallax.y + 12, parallax.y - 8],
          scale: [1, 1.06, 1],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute -right-24 top-32 h-[380px] w-[380px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(196,164,130,0.45) 0%, rgba(196,164,130,0) 70%)",
          willChange: "transform",
        }}
        animate={{
          x: [-parallax.x - 6, -parallax.x + 14, -parallax.x - 6],
          y: [-parallax.y + 4, -parallax.y - 10, -parallax.y + 4],
          scale: [1, 1.08, 1],
        }}
        transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Faint hex grid — subtle "navigation" feel without using mapbox. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='52' viewBox='0 0 60 52'%3E%3Cpath fill='none' stroke='%230f2634' stroke-width='0.6' d='M15 0 L45 0 L60 26 L45 52 L15 52 L0 26 Z'/%3E%3C/svg%3E\")",
          backgroundSize: "60px 52px",
        }}
      />

      {/* Mode-specific overlay — animates in / out on mode change. */}
      <AnimatePresence mode="wait">
        {mode === "searching" ? (
          <SearchingRadar key="searching" />
        ) : mode === "tracking" ? (
          <TrackingRoute key="tracking" driverActive={driverActive} />
        ) : (
          <IdleAccent key="idle" />
        )}
      </AnimatePresence>

      {/* Bottom soft fade so the booking sheet anchors naturally. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[40%]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(247,243,236,0) 0%, rgba(247,243,236,0.65) 60%, rgba(247,243,236,0.95) 100%)",
        }}
      />
    </div>
  );
}

/* ───────────────────────── variants ───────────────────────── */

function IdleAccent() {
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Centred Price mark — soft & subtle. */}
      <motion.div
        className="pointer-events-none absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 select-none"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 0.18, y: 0 }}
        transition={{ duration: 1.4, delay: 0.4 }}
      >
        <span className="font-[Outfit] text-[120px] font-semibold tracking-[-0.04em] text-foreground/30">
          Price
        </span>
      </motion.div>

      {/* Sparkle accent top-right */}
      <motion.div
        className="absolute right-[12%] top-[18%]"
        initial={{ opacity: 0, rotate: -12 }}
        animate={{ opacity: 1, rotate: 0 }}
        transition={{ delay: 1, duration: 1 }}
      >
        <motion.span
          animate={{ scale: [1, 1.18, 1], opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-block"
        >
          <Sparkles className="h-5 w-5 text-[#C4A482]" strokeWidth={2.2} />
        </motion.span>
      </motion.div>
    </motion.div>
  );
}

/** Sonar radar — 4 expanding rings + 5 car silhouettes that flicker in /
 *  out around the radar like vehicles being detected. The whole thing is
 *  positioned at ~38 % from the top so the lower 60 % stays clear for the
 *  booking sheet. */
function SearchingRadar() {
  // 5 cars positioned on a circle (in degrees from 12 o'clock, clockwise).
  // The pseudo-random delays give each car its own breath.
  const cars = [
    { angle: -65, delay: 0.0 },
    { angle: -20, delay: 0.7 },
    { angle: 30, delay: 1.4 },
    { angle: 75, delay: 0.4 },
    { angle: 130, delay: 1.0 },
  ];
  const RADIUS = 130; // px from centre to the car ring
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Gros wordmark "Price" dans le ciel — flotte doucement pour
          rappeler la marque pendant l'attente. */}
      <PriceSkyWordmark />

      {/* The radar — absolute, centred on top 38 %. */}
      <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2">
        {/* Expanding rings (4 of them, staggered) */}
        {[0, 1, 2, 3].map((i) => (
          <motion.span
            key={i}
            aria-hidden
            className="absolute left-1/2 top-1/2 rounded-full border-2 border-primary/50"
            style={{
              width: 100,
              height: 100,
              marginLeft: -50,
              marginTop: -50,
              boxShadow: "inset 0 0 20px rgba(14,92,91,0.18)",
            }}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: [0.4, 3.2], opacity: [0.85, 0] }}
            transition={{
              duration: 3.2,
              repeat: Infinity,
              ease: "easeOut",
              delay: i * 0.8,
            }}
          />
        ))}

        {/* Sweep arm — rotates 360° giving a real "radar" feel. Conic
            gradient masks itself into a slice with a fading tail. */}
        <motion.div
          aria-hidden
          className="absolute left-1/2 top-1/2 h-[260px] w-[260px] rounded-full"
          style={{
            marginLeft: -130,
            marginTop: -130,
            background:
              "conic-gradient(from 0deg, rgba(14,92,91,0.45) 0deg, rgba(14,92,91,0) 60deg, rgba(14,92,91,0) 360deg)",
            mask:
              "radial-gradient(circle, transparent 22px, black 22.5px, black 130px, transparent 130.5px)",
            WebkitMask:
              "radial-gradient(circle, transparent 22px, black 22.5px, black 130px, transparent 130.5px)",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
        />

        {/* Centre badge — Price wordmark + breathing pulse */}
        <div className="relative">
          <motion.span
            className="absolute -inset-2 rounded-full bg-primary/25"
            animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0, 0.55] }}
            transition={{ duration: 2.2, repeat: Infinity }}
            aria-hidden
          />
          <div className="relative grid h-14 w-14 place-items-center rounded-full bg-primary text-white shadow-[0_8px_22px_-6px_rgba(14,92,91,0.55)]">
            <span className="font-[Outfit] text-[13px] font-black tracking-[0.15em]">PRICE</span>
          </div>
        </div>

        {/* 5 cars distributed on the radius — fade in/out independently. */}
        {cars.map((c, i) => {
          const rad = (c.angle * Math.PI) / 180;
          const x = Math.sin(rad) * RADIUS;
          const y = -Math.cos(rad) * RADIUS;
          return (
            <motion.div
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{ x: x - 18, y: y - 14 }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0.7, 1, 1, 0.7],
              }}
              transition={{
                duration: 3.6,
                repeat: Infinity,
                delay: c.delay,
                ease: "easeInOut",
              }}
            >
              <CarSilhouette />
            </motion.div>
          );
        })}
      </div>

      {/* Caption underneath the radar */}
      <div className="absolute inset-x-0 top-[60%] flex flex-col items-center px-6 text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-primary/70">
          Service Price
        </p>
        <motion.p
          className="mt-2 font-[Outfit] text-[22px] font-semibold tracking-tight text-foreground"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Recherche d&apos;un chauffeur
          <Dots />
        </motion.p>
        <p className="mt-1 text-xs text-muted-foreground">
          Nous contactons les chauffeurs disponibles autour de vous
        </p>
      </div>
    </motion.div>
  );
}

/** Three jumping dots (typewriter feel). */
function Dots() {
  return (
    <span className="inline-block w-[1.6em] text-left text-foreground/70">
      <motion.span
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, times: [0, 0.2, 0.7, 1] }}
      >.</motion.span>
      <motion.span
        animate={{ opacity: [0, 0, 1, 1, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, times: [0, 0.2, 0.4, 0.7, 1] }}
      >.</motion.span>
      <motion.span
        animate={{ opacity: [0, 0, 0, 1, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, times: [0, 0.2, 0.5, 0.7, 1] }}
      >.</motion.span>
    </span>
  );
}

/** Big "Price" wordmark floating in the sky (top area of the hero).
 *  Used on both the searching and tracking modes so users clearly know
 *  they're on the Price app while the radar / route animation plays. */
function PriceSkyWordmark() {
  return (
    <motion.div
      className="pointer-events-none absolute left-1/2 top-[8%] -translate-x-1/2 select-none z-10"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      data-testid="price-sky-wordmark"
    >
      <motion.span
        className="block font-[Outfit] text-[54px] leading-none font-black tracking-[-0.03em] text-primary"
        style={{
          WebkitTextStroke: "1.5px #0F2634",
          textShadow: "0 3px 0 #0F2634, 0 6px 14px rgba(15,38,52,0.18)",
        }}
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      >
        Price
      </motion.span>
    </motion.div>
  );
}

/** Berline silhouette — luxe sedan profile, 36×28 px. */
function CarSilhouette() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="28"
      viewBox="0 0 36 28"
      fill="none"
      style={{ filter: "drop-shadow(0 3px 6px rgba(15,38,52,0.22))" }}
    >
      <path
        d="M2 19 L4 14 C5 12, 7 11, 9 11 L13 8 C14 7, 16 6.5, 18 6.5 L24 7 C27 7.5, 29 9, 31 11 L34 12 C35 12.3, 35.5 13, 35.5 14 L35.5 19 C35.5 20, 34.8 20.5, 34 20.5 L31.5 20.5 L31.5 19 C31.5 17, 30 15.5, 28 15.5 C26 15.5, 24.5 17, 24.5 19 L24.5 20.5 L11.5 20.5 L11.5 19 C11.5 17, 10 15.5, 8 15.5 C6 15.5, 4.5 17, 4.5 19 L4.5 20.5 L2 20.5 C1.2 20.5, 0.5 19.8, 0.5 19 Z"
        fill="#0F2634"
      />
      <circle cx="8" cy="20" r="2.4" fill="#0E5C5B" stroke="white" strokeWidth="0.8" />
      <circle cx="28" cy="20" r="2.4" fill="#0E5C5B" stroke="white" strokeWidth="0.8" />
      {/* small gold reflection on the windshield */}
      <path d="M13 9 L23 8.2 L26 11 L13 11 Z" fill="#C4A482" opacity="0.85" />
    </svg>
  );
}

/** Tracking mode — animated route + driver beacon. */
function TrackingRoute({ driverActive }) {
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Gros wordmark "Price" dans le ciel pendant le suivi du chauffeur */}
      <PriceSkyWordmark />
      <svg
        aria-hidden
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 400 800"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="hero-route" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0E5C5B" />
            <stop offset="100%" stopColor="#C4A482" />
          </linearGradient>
          <filter id="hero-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <motion.path
          d="M 80 180 C 140 240, 280 240, 320 360 C 340 440, 160 460, 200 560"
          fill="none"
          stroke="url(#hero-route)"
          strokeWidth="3.5"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.85 }}
          transition={{ duration: 2.4, ease: "easeInOut" }}
          filter="url(#hero-glow)"
        />
        {driverActive && (
          <motion.circle
            r="9"
            fill="#0F2634"
            stroke="white"
            strokeWidth="3"
            initial={{ offsetDistance: "0%" }}
            animate={{ offsetDistance: ["0%", "100%", "0%"] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            style={{
              offsetPath:
                "path('M 80 180 C 140 240, 280 240, 320 360 C 340 440, 160 460, 200 560')",
              offsetRotate: "auto",
            }}
          />
        )}
      </svg>

      {/* Caption underneath */}
      <div className="absolute inset-x-0 top-[60%] flex flex-col items-center px-6 text-center">
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-primary/70">
          Course en direct
        </p>
        <p className="mt-2 font-[Outfit] text-[22px] font-semibold tracking-tight text-foreground">
          Ton chauffeur arrive
        </p>
      </div>
    </motion.div>
  );
}
